import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Download,
  ExternalLink,
  FileArchive,
  FileSpreadsheet,
  FileText,
  HelpCircle,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Upload,
  Building2,
} from "lucide-react";
import toast from "react-hot-toast";
import { DsButton } from "../../design-system";
import { payrollOpsService } from "../../services/api";
import { getCurrentFinancialYear } from "./tax-planning/taxPlanning";
import { downloadForm24QCsv, downloadForm24QPdf } from "../../utils/form24qExport";
import { exportAndDownloadRpu, exportAndDownloadForm138Excel } from "../../utils/form24qRpuExport";
import { formatInr } from "./payroll/runPayrollUtils";
import Form24QChallanPanel from "./Form24QChallanPanel";
import DeductorMasterModal from "./DeductorMasterModal";
import Form24QPortDataModal from "./Form24QPortDataModal";

const WIZARD_STEPS = [
  { id: 1, key: "period", label: "Period", short: "1" },
  { id: 2, key: "review", label: "Review & validate", short: "2" },
  { id: 3, key: "challan", label: "Challans & Link", short: "3" },
  { id: 4, key: "rpu", label: "RPU & FVU", short: "4" },
  { id: 5, key: "filed", label: "Record filing", short: "5" },
];

const QUARTER_HINTS = {
  Q1: "Apr – Jun",
  Q2: "Jul – Sep",
  Q3: "Oct – Dec",
  Q4: "Jan – Mar",
};

const STATUS_STYLES = {
  draft: "bg-gray-100 text-gray-700 border-gray-200",
  ready: "bg-sky-50 text-sky-800 border-sky-200",
  filed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  DRAFT: "bg-gray-100 text-gray-700 border-gray-200",
  VALIDATED: "bg-sky-50 text-sky-800 border-sky-200",
  FVU_GENERATED: "bg-indigo-50 text-indigo-800 border-indigo-200",
  FILED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  REVISED: "bg-amber-50 text-amber-800 border-amber-200",
};

function fyOptions() {
  const current = getCurrentFinancialYear();
  const startYear = Number(String(current).slice(0, 4)) - 2;
  const out = [];
  for (let i = 0; i < 5; i += 1) {
    const y = startYear + i;
    out.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }
  return out.reverse();
}

function StepNav({ step, setStep, isFiled, unlockedKeys }) {
  return (
    <nav
      className="flex w-full flex-wrap items-stretch gap-1"
      aria-label="Form 24Q filing steps"
    >
      {WIZARD_STEPS.map((s, i) => {
        const active = step === s.id;
        const done = step > s.id;
        const correctionLocked =
          unlockedKeys && unlockedKeys.length > 0 && !unlockedKeys.includes(s.key) && s.key !== "filed";
        const locked = (isFiled && s.id < 5) || correctionLocked;
        return (
          <React.Fragment key={s.key}>
            <button
              type="button"
              disabled={locked && !active}
              onClick={() => !locked && setStep(s.id)}
              className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-xs font-semibold transition ${
                active
                  ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))] text-white shadow-sm"
                  : done
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))] hover:border-[hsl(var(--ds-primary)/0.35)] hover:text-[hsl(var(--ds-fg))]"
              } ${locked && !active ? "cursor-not-allowed opacity-40" : ""}`}
            >
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                  active
                    ? "bg-white/20"
                    : done
                      ? "bg-emerald-500/15 text-emerald-700"
                      : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                }`}
              >
                {done ? <CheckCircle2 size={14} /> : s.short}
              </span>
              <span className="min-w-0 truncate">{s.label}</span>
            </button>
            {i < WIZARD_STEPS.length - 1 ? (
              <ChevronRight
                size={14}
                className="mt-3 hidden shrink-0 text-[hsl(var(--ds-muted-fg))] lg:block"
                aria-hidden
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

function ValidationList({ items, type = "error" }) {
  if (!items?.length) return null;
  const isError = type === "error";
  return (
    <ul
      className={`space-y-1.5 rounded-lg border p-3 text-xs ${
        isError
          ? "border-red-200 bg-red-50 text-red-900"
          : "border-amber-200 bg-amber-50 text-amber-900"
      }`}
    >
      {items.map((item, i) => (
        <li key={i} className="flex gap-2">
          {isError ? (
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          ) : (
            <HelpCircle size={14} className="mt-0.5 shrink-0" />
          )}
          <span>
            {item.field ? (
              <span className="font-mono text-[10px] opacity-70">[{item.field}] </span>
            ) : null}
            {item.message}
          </span>
        </li>
      ))}
    </ul>
  );
}

function InstructionCard({ darkMode, fvuEntry }) {
  const portal = fvuEntry?.nsdlPortalUrl || "https://www.tdscpc.gov.in/";
  const fvuUrl = fvuEntry?.downloadUrl || portal;

  return (
    <div
      className={`rounded-lg border p-4 text-xs leading-relaxed ${
        darkMode ? "border-sky-800/40 bg-sky-950/20 text-sky-100" : "border-sky-200 bg-sky-50 text-sky-900"
      }`}
    >
      <p className="mb-2 font-semibold">How to file via NSDL / TRACES</p>
      <ol className="ml-4 list-decimal space-y-1.5">
        <li>
          Download the latest <strong>FVU</strong> from{" "}
          <a href={fvuUrl} target="_blank" rel="noopener noreferrer" className="underline">
            TIN-NSDL / TRACES
          </a>
          .
        </li>
        <li>
          Prefer <strong>Run official FVU</strong> in step 4 (requires <code>FVU_JAR_PATH</code> on the
          server), or download the RPU ZIP and validate manually.
        </li>
        <li>
          Upload the <strong>.fvu</strong> on{" "}
          <a href={portal} target="_blank" rel="noopener noreferrer" className="underline">
            NSDL / TRACES
          </a>
          .
        </li>
        <li>Return here (step 5) to record the PRN / acknowledgement.</li>
      </ol>
    </div>
  );
}

export default function Form24QFilingPanel({ darkMode = false }) {
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [quarter, setQuarter] = useState("Q1");
  const [returnType, setReturnType] = useState("REGULAR");
  const [originalPrn, setOriginalPrn] = useState("");
  const [fvuVersion, setFvuVersion] = useState("");
  const [fvuLocked, setFvuLocked] = useState(true);
  const [fvuWarning, setFvuWarning] = useState(null);
  const [fvuCatalog, setFvuCatalog] = useState([]);
  const [correctionTypesCatalog, setCorrectionTypesCatalog] = useState([]);
  const [fvuResolved, setFvuResolved] = useState(null);
  const [salaryRegime, setSalaryRegime] = useState(null);
  const [step, setStep] = useState(1);
  const [summary, setSummary] = useState(null);
  const [validation, setValidation] = useState(null);
  const [review, setReview] = useState(null);
  const [filing, setFiling] = useState(null);
  const [filings, setFilings] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [fvuResult, setFvuResult] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [correctionTypes, setCorrectionTypes] = useState([]);
  const [challan, setChallan] = useState({
    challanNumber: "",
    challanDate: "",
    bsrCode: "",
    bankName: "",
    challanAmount: "",
    interestAmount: "",
    remarks: "",
  });
  const [nsdl, setNsdl] = useState({
    nsdlToken: "",
    nsdlAckNumber: "",
    nsdlFiledDate: "",
    provisionalReceiptNumber: "",
  });
  const [deductorModalOpen, setDeductorModalOpen] = useState(false);
  const [portDataModalOpen, setPortDataModalOpen] = useState(false);
  const [portData, setPortData] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);

  const card =
    "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]";

  const inputCls =
    "mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 text-sm text-[hsl(var(--ds-fg))]";

  const loadFvuCatalog = useCallback(async () => {
    try {
      const res = await payrollOpsService.getFvuCatalog(financialYear, quarter);
      const catalog = res?.data?.catalog || [];
      setFvuCatalog(catalog);
      setCorrectionTypesCatalog(res?.data?.correctionTypes || []);
      const resolved = res?.data?.resolved;
      setFvuResolved(resolved || null);
      setSalaryRegime(res?.data?.regime || null);
      if (resolved?.version) {
        setFvuVersion(resolved.version);
        setFvuLocked(true);
        setFvuWarning(resolved.warning || null);
      } else {
        setFvuVersion((prev) => prev || res?.data?.defaultVersion || catalog[0]?.version || "9.5");
      }
    } catch {
      setFvuCatalog([
        {
          version: "9.5",
          label: "FVU 9.5 — Form 24Q (upto FY 2025-26)",
          isDefault: true,
          formLabel: "Form 24Q",
          periodLabel: "Financial Year",
        },
      ]);
      setFvuVersion("9.5");
      setSalaryRegime({
        regime: "legacy",
        formCode: "24Q",
        formLabel: "Form 24Q",
        periodLabel: "Financial Year",
      });
    }
  }, [financialYear, quarter]);

  const loadFilings = useCallback(async () => {
    try {
      const res = await payrollOpsService.listTdsFilings(financialYear);
      setFilings(res?.data || []);
    } catch {
      setFilings([]);
    }
  }, [financialYear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!filing?.id) {
        setArtifacts([]);
        return;
      }
      try {
        const art = await payrollOpsService.listForm24QArtifacts(filing.id);
        if (!cancelled) setArtifacts(art?.data || art || []);
      } catch {
        if (!cancelled) setArtifacts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filing?.id]);

  const syncFilingState = useCallback((f) => {
    if (!f) return;
    setFiling(f);
    setReturnType(f.returnType || "REGULAR");
    setOriginalPrn(f.originalPrn || "");
    setCorrectionTypes(Array.isArray(f.correctionTypesJson) ? f.correctionTypesJson : []);
    setChallan({
      challanNumber: f.challanNumber || "",
      challanDate: f.challanDate ? String(f.challanDate).slice(0, 10) : "",
      bsrCode: f.bsrCode || "",
      bankName: f.bankName || "",
      challanAmount: f.totalTds != null ? String(f.totalTds) : "",
      interestAmount: f.interestAmount != null ? String(f.interestAmount) : "",
      remarks: f.remarks || "",
    });
    setNsdl({
      nsdlToken: f.nsdlToken || "",
      nsdlAckNumber: f.nsdlAckNumber || "",
      nsdlFiledDate: f.nsdlFiledDate ? String(f.nsdlFiledDate).slice(0, 10) : "",
      provisionalReceiptNumber: f.provisionalReceiptNumber || f.nsdlAckNumber || "",
    });
    if (f.fvuVersion) setFvuVersion(f.fvuVersion);
    const snap = f.snapshotJson;
    if (snap?.portData) setPortData(snap.portData);
    // Prefer filing snapshot so Quick-open does not keep another quarter's in-memory summary
    if (snap && (snap.employeeAggregates || snap.rows)) {
      const deductorTan = f.deductorSnapshotJson?.tan;
      setSummary(
        deductorTan
          ? {
              ...snap,
              employer: { ...(snap.employer || {}), tan: deductorTan },
            }
          : snap
      );
    }
  }, []);

  const runValidation = useCallback(
    async (requireChallan = false) => {
      setLoading(true);
      try {
        const res = await payrollOpsService.validateForm24QFiling({
          financialYear,
          quarter,
          requireChallan,
          ...challan,
          challanAmount: challan.challanAmount || summary?.totalTds,
        });
        const data = res?.data || res;
        setSummary(data.summary || null);
        setValidation(data.validation || null);
        return data;
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Validation failed");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [financialYear, quarter, challan, summary?.totalTds]
  );

  const runReviewValidate = async () => {
    if (!filing?.id) {
      toast.error("Prepare the return in step 1 first");
      return;
    }
    setLoading(true);
    try {
      const res = await payrollOpsService.validateForm24QReview(filing.id);
      const data = res?.data || res;
      setReview(data.review || null);
      if (data.filing) syncFilingState(data.filing);
      if (data.review?.canProceed) {
        toast.success("Review validated — proceed to challan");
      } else {
        toast.error("Resolve ERROR items before proceeding");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Review validation failed");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollOpsService.getForm24Q(financialYear, quarter);
      const data = res?.data || res;
      setSummary(data);
      const existing = filings.find(
        (f) =>
          f.financialYear === financialYear &&
          f.quarter === quarter &&
          (f.returnType || "REGULAR") === returnType
      );
      if (existing) syncFilingState(existing);
      await runValidation(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Unable to load 24Q summary");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [financialYear, quarter, filings, syncFilingState, runValidation, returnType]);

  useEffect(() => {
    loadFvuCatalog();
  }, [loadFvuCatalog]);

  useEffect(() => {
    setPortData(null);
  }, [financialYear, quarter]);

  useEffect(() => {
    loadFilings();
  }, [loadFilings]);

  useEffect(() => {
    (async () => {
      try {
        const res = await payrollOpsService.getEmployees();
        const data = res?.data?.employees || res?.data || [];
        setEmployeeList(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (step >= 2) loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, financialYear, quarter]);

  const handlePrepare = async () => {
    if (returnType === "CORRECTION" && !originalPrn.trim()) {
      toast.error("Original PRN is required for correction returns");
      return;
    }
    if (!portData?.months?.length) {
      toast.error("Open Port Data and confirm quarter dates before preparing");
      setPortDataModalOpen(true);
      return;
    }
    if (!portData?.canProceed) {
      toast.error("Port Data has unresolved interest/penalty or Q4 errors — re-open Port Data");
      setPortDataModalOpen(true);
      return;
    }
    if (portData?.interestPenalty?.requiresPayment && !portData?.acknowledged) {
      toast.error("Acknowledge interest / penalty in Port Data before preparing");
      setPortDataModalOpen(true);
      return;
    }
    if (fvuWarning && !window.confirm(`${fvuWarning}\n\nContinue with this FVU version?`)) {
      return;
    }
    setLoading(true);
    try {
      const res = await payrollOpsService.prepareTdsFiling({
        financialYear,
        quarter,
        fvuVersion,
        returnType,
        originalPrn: returnType === "CORRECTION" ? originalPrn.trim() : undefined,
        portData,
      });
      const data = res?.data || res;
      setFiling(data.filing || null);
      setSummary(data.summary || summary);
      setValidation(data.validation || null);
      setReview(data.review || null);
      if (data.fvuWarning) setFvuWarning(data.fvuWarning);
      toast.success("Return prepared — review validation results");
      await loadFilings();
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Prepare failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExportRpu = async () => {
    const hasChallan =
      challan.challanNumber ||
      (bundle?.challans || []).length > 0 ||
      filing?.challanNumber;
    if (!hasChallan) {
      toast.error("Add at least one challan in step 3 first");
      setStep(3);
      return;
    }
    setLoading(true);
    try {
      const primary = (bundle?.challans || [])[0];
      const filename = await exportAndDownloadRpu(payrollOpsService, {
        financialYear,
        quarter,
        fvuVersion,
        filingId: filing?.id || undefined,
        challanNumber: primary?.challanSerialNumber || challan.challanNumber,
        challanDate: primary?.dateOfDeposit
          ? String(primary.dateOfDeposit).slice(0, 10)
          : challan.challanDate,
        bsrCode: primary?.bsrCode || challan.bsrCode,
        bankName: primary?.bankName || challan.bankName,
        interestAmount: primary?.interestAmount || challan.interestAmount || 0,
        challanAmount:
          primary?.totalAmountDeposited ||
          primary?.tdsAmount ||
          challan.challanAmount ||
          summary?.totalTds,
      });
      toast.success(`RPU ZIP downloaded — ${filename}`);
      await loadFilings();
    } catch (e) {
      toast.error(e?.message || "RPU export failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExportForm138Excel = async () => {
    if (!filing?.id) {
      toast.error("Prepare the return first");
      return;
    }
    if (!(bundle?.challans || []).length) {
      toast.error("Add at least one challan in step 3 first");
      setStep(3);
      return;
    }
    if (bundle && !bundle.reconciliation?.balanced) {
      toast.error("Fix challan ↔ deductee mapping before downloading Form 138 Excel");
      setStep(3);
      return;
    }
    setLoading(true);
    try {
      const filename = await exportAndDownloadForm138Excel(payrollOpsService, {
        financialYear,
        quarter,
        filingId: filing.id,
      });
      toast.success(`Form 138 Excel downloaded — ${filename}`);
    } catch (e) {
      toast.error(e?.message || "Form 138 Excel export failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRunFvu = async () => {
    if (!filing?.id) {
      toast.error("Prepare the return first");
      return;
    }
    if (bundle && !bundle.reconciliation?.balanced) {
      toast.error("Fix challan ↔ deductee mapping before running FVU");
      setStep(3);
      return;
    }
    setLoading(true);
    try {
      const res = await payrollOpsService.runForm24QFvu(filing.id);
      const data = res?.data || res;
      setFvuResult(data);
      const manualFallbackCode = data?.code || data?.data?.code;
      if (data?.success) {
        toast.success("FVU generated successfully");
        syncFilingState({
          ...filing,
          workflowStatus: "FVU_GENERATED",
          status: "ready",
          fvuFilePath: data.fvuFilePath,
          form27aPath: data.form27aPath,
          engineVersionId: data.engine?.id || filing.engineVersionId,
          rpuSpecKey: data.engine?.rpuSpecKey || filing.rpuSpecKey,
          fvuVersion: data.engine?.fvuVersion || filing.fvuVersion,
        });
        try {
          const art = await payrollOpsService.listForm24QArtifacts(filing.id);
          setArtifacts(art?.data || art || []);
        } catch {
          /* non-fatal */
        }
      } else if (data?.mode === "manual") {
        const msg = data.message || "Configure FVU_JAR_PATH or validate RPU manually";
        toast(msg, { icon: "ℹ️", duration: 10000 });
        if (manualFallbackCode === "FVU_AUTO_VALIDATE_UNAVAILABLE") {
          const attempts = (data.attempts || data.parsedErrors || [])
            .map((a) => (typeof a === "string" ? a : a.message))
            .filter(Boolean)
            .slice(0, 6)
            .join("\n");
          const jump = window.confirm(
            "Server cannot auto-build .fvu + Form 27A on this machine (Protean FVU batch mode returns T-FV-1022 for raw RPU).\n\n" +
              "To file TY 2026-27 Q1 (Form 138 / FVU 1.1):\n" +
              "1) Cancel this dialog\n" +
              "2) Download RPU ZIP from Step 4\n" +
              "3) Download official .csi from Income Tax e-Filing (Challan Status Inquiry) — do not rely on PaceHRM-synthesized CSI\n" +
              "4) Open desktop Protean FVU 1.1 (TDS_STANDALONE_FVU_1.1.bat), browse the .txt + .csi, Validate\n" +
              "5) Use the generated .fvu + Form 27A on TRACES / NSDL\n" +
              "6) Return here → Step 5 and record PRN\n\n" +
              (attempts ? `Server log:\n${attempts}\n\n` : "") +
              "Click OK only if you already have a .fvu and want to jump to Step 5 (PRN)."
          );
          if (jump) setStep(5);
        }
        try {
          const art = await payrollOpsService.listForm24QArtifacts(filing.id);
          setArtifacts(art?.data || art || []);
        } catch {
          /* non-fatal */
        }
      } else {
        toast.error(data?.message || "FVU validation failed");
      }
    } catch (e) {
      const proxyMsg = String(e?.message || e?.response?.data || "");
      if (/proxy|ECONNRESET|ECONNABORTED|timeout|Network Error/i.test(proxyMsg)) {
        toast.error(
          "FVU run timed out or lost connection to the API. Restart frontend (craco proxy timeout) and backend, then retry. Or Download RPU ZIP and validate in desktop FVU.",
          { duration: 8000 }
        );
      } else {
        toast.error(e?.response?.data?.message || e.message || "FVU run failed");
      }
      if (e?.response?.data?.reconciliation) {
        setBundle((prev) => ({
          ...prev,
          reconciliation: e.response.data.reconciliation,
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async () => {
    const prn =
      nsdl.provisionalReceiptNumber || nsdl.nsdlAckNumber || nsdl.nsdlToken;
    if (!prn) {
      toast.error("Enter PRN / NSDL token or acknowledgement number");
      return;
    }
    if (bundle && !bundle.reconciliation?.balanced) {
      toast.error("Reconciliation unbalanced — fix mappings before recording");
      setStep(3);
      return;
    }
    setLoading(true);
    try {
      const res = await payrollOpsService.fileTdsReturn({
        filingId: filing?.id,
        financialYear,
        quarter,
        fvuVersion,
        ...challan,
        ...nsdl,
        provisionalReceiptNumber: prn,
        interestAmount: Number(challan.interestAmount) || 0,
        challanAmount: Number(challan.challanAmount) || summary?.totalTds,
      });
      const data = res?.data || null;
      syncFilingState(data);
      toast.success(res?.message || "Filing recorded");
      await loadFilings();
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Filing failed");
    } finally {
      setLoading(false);
    }
  };

  const handleStartCorrection = async () => {
    if (!filing?.id) return;
    if (!correctionTypes.length) {
      toast.error("Select at least one correction type (C1–C5)");
      return;
    }
    const prn =
      originalPrn ||
      filing.provisionalReceiptNumber ||
      filing.nsdlAckNumber ||
      "";
    if (!prn) {
      toast.error("Original PRN is required");
      return;
    }
    setLoading(true);
    try {
      const res = await payrollOpsService.startForm24QCorrection({
        parentFilingId: filing.id,
        originalPrn: prn,
        correctionTypes,
      });
      const data = res?.data || res;
      syncFilingState(data.filing);
      setBundle(data.bundle || null);
      setReturnType("CORRECTION");
      toast.success(
        `Correction #${data.filing?.correctionSequence || ""} started from Conso clone`
      );
      await loadFilings();
      const unlocks = (correctionTypesCatalog || [])
        .filter((c) => correctionTypes.includes(c.code))
        .flatMap((c) => c.unlocks || []);
      if (unlocks.includes("challan")) setStep(3);
      else if (unlocks.includes("review")) setStep(2);
      else setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Unable to start correction");
    } finally {
      setLoading(false);
    }
  };

  const aggregates = summary?.employeeAggregates || [];
  const dataConflicts = summary?.dataConflicts || [];
  const blockingConflicts = dataConflicts.filter(
    (c) => c.type === "DUPLICATE_EMP_CODE" || c.severity === "error"
  );
  const diagnostics = summary?.diagnostics || null;
  const isFiled =
    filing?.status === "filed" ||
    filing?.workflowStatus === "FILED" ||
    filing?.workflowStatus === "REVISED";
  const fvuEntry = useMemo(
    () => fvuCatalog.find((c) => c.version === fvuVersion) || fvuResolved || fvuCatalog[0],
    [fvuCatalog, fvuVersion, fvuResolved]
  );

  const unlockedKeys = useMemo(() => {
    if (filing?.returnType !== "CORRECTION") return null;
    const types = Array.isArray(filing.correctionTypesJson)
      ? filing.correctionTypesJson
      : correctionTypes;
    if (!types.length) return ["period", "review", "challan", "rpu", "filed"];
    const keys = new Set(["period", "rpu", "filed"]);
    (correctionTypesCatalog || [])
      .filter((c) => types.includes(c.code))
      .forEach((c) => (c.unlocks || []).forEach((k) => keys.add(k)));
    return [...keys];
  }, [filing, correctionTypes, correctionTypesCatalog]);

  const employer = {
    ...(summary?.employer || {}),
    // Deductor Details / filing snapshot TAN wins over Company.tanNo (legacy mismatch)
    tan:
      filing?.deductorSnapshotJson?.tan ||
      summary?.employer?.tan ||
      "",
  };
  const statusLabel = filing?.workflowStatus || filing?.status || "draft";
  const formLabel =
    salaryRegime?.formLabel ||
    fvuResolved?.formLabel ||
    (filing?.formType === "138" ? "Form No. 138" : "Form 24Q");
  const periodLabel =
    salaryRegime?.periodLabel || fvuResolved?.periodLabel || "Financial Year";
  const isNewRegime = (salaryRegime?.regime || fvuResolved?.regime) === "new";

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden">
      <div className={`${card} shrink-0 p-4`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">{formLabel} — Quarterly TDS Return</h3>
            <p className="mt-1 max-w-3xl text-xs text-[hsl(var(--ds-muted-fg))]">
              {isNewRegime
                ? "Tax Year 2026-27 onwards · FVU 1.1 · Port payroll TDS → prepare RPU → validate with FVU → upload to NSDL / TRACES."
                : "Upto FY 2025-26 · FVU 9.5 · Port payroll TDS → prepare RPU → validate with FVU → upload to NSDL / TRACES."}
            </p>
            {employer.tan ? (
              <p className="mt-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                Deductor TAN:{" "}
                <span className="font-mono font-medium text-[hsl(var(--ds-fg))]">{employer.tan}</span>
                {employer.name ? (
                  <>
                    {" · "}
                    <span className="font-medium text-[hsl(var(--ds-fg))]">{employer.name}</span>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {filing ? (
              <span
                className={`rounded-md border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                  STATUS_STYLES[statusLabel] || STATUS_STYLES.draft
                }`}
              >
                {statusLabel}
                {filing.returnType === "CORRECTION"
                  ? ` · C#${filing.correctionSequence || ""}`
                  : ""}
              </span>
            ) : null}
            <DsButton type="button" variant="outline" onClick={() => setDeductorModalOpen(true)}>
              <Building2 size={14} />
              Deductor / RP
            </DsButton>
            <DsButton type="button" variant="outline" onClick={() => setPortDataModalOpen(true)}>
              {portData?.canProceed ? (
                <CheckCircle2 size={14} className="text-emerald-600" />
              ) : (
                <AlertTriangle size={14} className="text-amber-600" />
              )}
              Port Data
            </DsButton>
          </div>
        </div>
        <div className="mt-4">
          <StepNav
            step={step}
            setStep={setStep}
            isFiled={isFiled && filing?.returnType !== "CORRECTION"}
            unlockedKeys={unlockedKeys}
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">

      {/* Step 1 — Period */}
      {step === 1 && (
        <div className="grid min-h-0 flex-1 gap-4 overflow-hidden lg:grid-cols-12">
          <div className={`${card} flex min-h-0 flex-col overflow-hidden p-4 lg:col-span-8`}>
            <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">Select return period</h4>
              <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Choose FY / quarter, then prepare from payroll TDS.
              </p>
            </div>
            {portData?.interestPenalty?.requiresPayment && (
              <div className="mb-3 shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <AlertTriangle size={14} className="mr-1 inline" />
                Interest / penalty {formatInr(portData.interestPenalty.totalPayable)} detected —
                open{" "}
                <button
                  type="button"
                  className="font-semibold underline"
                  onClick={() => setPortDataModalOpen(true)}
                >
                  Port Data
                </button>{" "}
                to review, then pay via challan in Step 3.
                {(portData.interestPenalty.details || []).slice(0, 3).map((d, i) => (
                  <div key={i} className="mt-1 pl-4">
                    • {d.message} — {formatInr(d.amount)}
                  </div>
                ))}
              </div>
            )}
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                  {periodLabel}
                  <select
                    value={financialYear}
                    onChange={(e) => setFinancialYear(e.target.value)}
                    className={inputCls}
                  >
                    {fyOptions().map((fy) => (
                      <option key={fy} value={fy}>
                        {fy}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                  Quarter
                  <select
                    value={quarter}
                    onChange={(e) => setQuarter(e.target.value)}
                    className={inputCls}
                  >
                    {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                      <option key={q} value={q}>
                        {q} ({QUARTER_HINTS[q]})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                  FVU version
                  <select
                    value={fvuVersion}
                    disabled={fvuLocked}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFvuVersion(v);
                      const resolved = fvuResolved?.version;
                      if (resolved && v !== resolved) {
                        setFvuWarning(
                          `Selected FVU ${v} differs from recommended ${resolved} for ${financialYear} ${quarter}.`
                        );
                      } else {
                        setFvuWarning(null);
                      }
                    }}
                    className={`${inputCls} ${fvuLocked ? "opacity-80" : ""}`}
                  >
                    {fvuCatalog.map((v) => (
                      <option key={v.version} value={v.version}>
                        {v.label || `FVU ${v.version}`}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="mt-1 text-[10px] text-[hsl(var(--ds-muted-fg))] underline"
                    onClick={() => setFvuLocked((p) => !p)}
                  >
                    {fvuLocked
                      ? "Override version (only if allowed for this period)"
                      : "Lock to recommended"}
                  </button>
                  {salaryRegime?.correctionFormatVersion && returnType === "CORRECTION" ? (
                    <span className="mt-1 block text-[10px] text-[hsl(var(--ds-muted-fg))]">
                      Correction file format: {salaryRegime.formLabel || formLabel} v
                      {salaryRegime.correctionFormatVersion}
                    </span>
                  ) : null}
                </label>
              </div>

              {fvuWarning && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <AlertTriangle size={14} className="mr-1 inline" />
                  {fvuWarning}
                </div>
              )}

              <div>
                <p className="mb-2 text-xs font-medium text-[hsl(var(--ds-muted-fg))]">Return type</p>
                <div className="flex flex-wrap gap-2">
                  {["REGULAR", "CORRECTION"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setReturnType(t)}
                      className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${
                        returnType === t
                          ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))]"
                          : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      {t === "REGULAR" ? "Regular" : "Correction"}
                    </button>
                  ))}
                </div>
                {returnType === "CORRECTION" && (
                  <label className="mt-3 block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                    Original Provisional Receipt Number (PRN) *
                    <input
                      value={originalPrn}
                      onChange={(e) => setOriginalPrn(e.target.value)}
                      className={inputCls}
                      placeholder="From previously filed return / TRACES"
                    />
                    <span className="mt-1 block text-[10px] text-[hsl(var(--ds-muted-fg))]">
                      Correction must be started from a FILED return (File Correction on step 5) so
                      Conso data is cloned — do not build corrections from scratch.
                    </span>
                  </label>
                )}
              </div>

              {fvuEntry?.notes && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))]">{fvuEntry.notes}</p>
              )}
            </div>

            <div className="mt-4 flex shrink-0 flex-wrap gap-2 border-t border-[hsl(var(--ds-border))] pt-3">
              <DsButton type="button" variant="primary" onClick={handlePrepare} disabled={loading}>
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                Prepare return
              </DsButton>
              <DsButton type="button" variant="outline" onClick={() => setStep(2)}>
                Skip to review <ArrowRight size={14} />
              </DsButton>
            </div>
          </div>

          <div className={`${card} flex min-h-0 flex-col overflow-hidden lg:col-span-4`}>
            <div className="shrink-0 border-b border-[hsl(var(--ds-border))] px-4 py-3">
              <h4 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
                Filings — {financialYear}
              </h4>
              <p className="mt-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Open a saved draft or filed return from the database
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              {filings.length === 0 ? (
                <div className="flex h-full min-h-[160px] items-center justify-center p-6 text-center text-xs text-[hsl(var(--ds-muted-fg))]">
                  No filings for this year yet. Prepare a return to create one.
                </div>
              ) : (
                <table className="ds-table w-full text-xs">
                  <thead className="sticky top-0 z-10 bg-[hsl(var(--ds-muted))]">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Quarter</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">TDS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filings.map((f) => (
                      <tr
                        key={
                          f.id ||
                          `${f.financialYear}-${f.quarter}-${f.returnType}-${f.correctionSequence}`
                        }
                        className={`cursor-pointer border-t border-[hsl(var(--ds-border))] transition hover:bg-[hsl(var(--ds-muted)/0.45)] ${
                          f.quarter === quarter && (f.returnType || "REGULAR") === returnType
                            ? "bg-[hsl(var(--ds-primary)/0.06)]"
                            : ""
                        }`}
                        onClick={() => {
                          setQuarter(f.quarter);
                          setReturnType(f.returnType || "REGULAR");
                          syncFilingState(f);
                          setStep(f.status === "filed" || f.workflowStatus === "FILED" ? 5 : 2);
                        }}
                      >
                        <td className="px-3 py-2.5 font-semibold">{f.quarter}</td>
                        <td className="px-3 py-2.5">
                          {f.returnType === "CORRECTION"
                            ? `C#${f.correctionSequence || ""}`
                            : "Regular"}
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                              STATUS_STYLES[f.workflowStatus || f.status] || STATUS_STYLES.draft
                            }`}
                          >
                            {f.workflowStatus || f.status}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {formatInr(f.totalTds)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 2 — Review */}
      {step === 2 && (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
          <div className="grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Total TDS", formatInr(summary?.totalTds)],
              ["Employees", summary?.employeeCount ?? 0],
              ["Period", summary?.quarterLabel || quarter],
              ["Employer TAN", employer.tan || "—"],
            ].map(([label, value]) => (
              <div key={label} className={`${card} p-3`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  {label}
                </p>
                <p
                  className={`mt-1 text-sm font-bold leading-tight ${
                    label === "Employer TAN" ? "font-mono" : "tabular-nums"
                  }`}
                >
                  {value}
                </p>
              </div>
            ))}
          </div>

          <ValidationList
            items={(review?.errors || validation?.errors || []).filter(
              (e) => e?.code !== "ORPHAN_TDS_ROWS"
            )}
            type="error"
          />
          <ValidationList
            items={(review?.warnings || validation?.warnings || []).filter(
              (e) => e?.code !== "ORPHAN_TDS_ROWS"
            )}
            type="warning"
          />

          {blockingConflicts.length > 0 && (
            <div className="shrink-0 space-y-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-xs text-red-950">
              <p className="font-semibold">
                Blocking master / ledger conflict
                {diagnostics?.inflatedBy > 0
                  ? ` — raw sum was inflated by ${formatInr(diagnostics.inflatedBy)}`
                  : ""}
              </p>
              {blockingConflicts.map((c, idx) => (
                <div key={`${c.type}-${c.empCode || idx}`} className="rounded border border-red-200 bg-white/70 p-2">
                  <p className="font-medium">{c.message}</p>
                  {(c.identities || []).length > 0 && (
                    <ul className="mt-1 space-y-0.5 font-mono text-[11px]">
                      {c.identities.map((i) => (
                        <li key={`${i.employeeId}-${i.empCode}`}>
                          empId={i.employeeId}
                          {i.empCode ? ` code=${i.empCode}` : ""}
                          {i.employmentStatus ? ` status=${i.employmentStatus}` : ""}
                          {` pan=${i.pan || "Missing"}`}
                          {` tds=${formatInr(i.totalTds)}`}
                          {i.months?.length ? ` months=${i.months.join(",")}` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              <p className="text-[11px] text-red-800">
                Same emp code maps to multiple employee IDs in this company. Fix Employee master,
                then re-prepare.
              </p>
            </div>
          )}

          {(review?.canProceed || validation?.valid) && blockingConflicts.length === 0 && (
            <div className="flex shrink-0 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              <CheckCircle2 size={16} />
              Validation passed — proceed to challan details
            </div>
          )}

          <div className={`${card} flex min-h-0 flex-1 flex-col overflow-hidden`}>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="ds-table w-full min-w-[760px] text-xs">
                <thead className="sticky top-0 z-10 bg-[hsl(var(--ds-muted))] shadow-[0_1px_0_hsl(var(--ds-border))]">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Sl No</th>
                    <th className="px-3 py-2 text-left font-medium">Emp ID</th>
                    <th className="px-3 py-2 text-left font-medium">Code</th>
                    <th className="px-3 py-2 text-left font-medium">Name</th>
                    <th className="px-3 py-2 text-left font-medium">PAN</th>
                    <th className="px-3 py-2 text-left font-medium">Regime</th>
                    <th className="px-3 py-2 text-right font-medium">Quarter TDS</th>
                    <th className="px-3 py-2 text-left font-medium">Months</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-[hsl(var(--ds-muted-fg))]">
                        <Loader2 size={20} className="mx-auto animate-spin" />
                      </td>
                    </tr>
                  ) : aggregates.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-[hsl(var(--ds-muted-fg))]">
                        No TDS records for this quarter. Process payroll with TDS first.
                      </td>
                    </tr>
                  ) : (
                    aggregates.map((r, idx) => (
                      <tr key={r.employeeId} className="border-t border-[hsl(var(--ds-border))]">
                        <td className="px-3 py-2 tabular-nums text-[hsl(var(--ds-muted-fg))]">
                          {idx + 1}
                        </td>
                        <td className="px-3 py-2 font-mono tabular-nums">{r.employeeId}</td>
                        <td className="px-3 py-2">{r.empCode}</td>
                        <td className="px-3 py-2">{r.name}</td>
                        <td className={`px-3 py-2 font-mono ${!r.pan ? "text-red-500" : ""}`}>
                          {r.pan || "Missing"}
                        </td>
                        <td className="px-3 py-2">{r.taxRegime}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatInr(r.totalTds)}</td>
                        <td className="px-3 py-2 text-[hsl(var(--ds-muted-fg))]">
                          {(r.months || []).join(", ")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2 border-t border-[hsl(var(--ds-border))] pt-3">
            <DsButton
              type="button"
              variant="outline"
              disabled={loading}
              onClick={() => runValidation(false)}
            >
              <RefreshCw size={14} /> Re-validate payroll
            </DsButton>
            <DsButton
              type="button"
              variant="outline"
              disabled={loading || !filing?.id}
              onClick={runReviewValidate}
            >
              <ShieldCheck size={14} /> Pre-validate (block errors)
            </DsButton>
            <DsButton
              type="button"
              variant="outline"
              disabled={!summary}
              onClick={() => {
                downloadForm24QPdf(summary);
                toast.success("PDF summary downloaded");
              }}
            >
              <FileText size={14} /> PDF summary
            </DsButton>
            <DsButton
              type="button"
              variant="outline"
              disabled={!summary}
              onClick={() => {
                downloadForm24QCsv(summary);
                toast.success("CSV downloaded");
              }}
            >
              <FileSpreadsheet size={14} /> CSV (review)
            </DsButton>
            <DsButton
              type="button"
              variant="primary"
              className="ml-auto"
              disabled={
                !(review?.canProceed || validation?.valid) || blockingConflicts.length > 0
              }
              onClick={() => setStep(3)}
            >
              Challan details <ArrowRight size={14} />
            </DsButton>
          </div>
        </div>
      )}

      {/* Step 3 — TDS Challan Management (register + Link deductions) */}
      {step === 3 && (
        <div className={`${card} flex min-h-0 flex-1 flex-col overflow-hidden`}>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <Form24QChallanPanel
              filingId={filing?.id}
              financialYear={financialYear}
              quarter={quarter}
              darkMode={darkMode}
              legacyChallan={challan}
              onLegacyChallanChange={setChallan}
              onBundleChange={setBundle}
            />
          </div>
          <div className="flex shrink-0 flex-wrap gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
            <DsButton type="button" variant="outline" onClick={() => setStep(2)}>
              Back
            </DsButton>
            <DsButton
              type="button"
              variant="primary"
              className="ml-auto"
              disabled={bundle && !bundle.reconciliation?.balanced}
              onClick={() => setStep(4)}
            >
              RPU & FVU <ArrowRight size={14} />
            </DsButton>
          </div>
        </div>
      )}

      {/* Step 4 — RPU & FVU */}
      {step === 4 && (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          <InstructionCard darkMode={darkMode} fvuEntry={fvuEntry} />

          <div className={`${card} p-5`}>
            <h3 className="mb-3 text-sm font-semibold text-[hsl(var(--ds-fg))]">
              Generate RPU / run official FVU
            </h3>
            <div className="mb-4 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg bg-[hsl(var(--ds-muted))] p-3">
                <p className="text-[hsl(var(--ds-muted-fg))]">FVU version</p>
                <p className="font-semibold">{fvuVersion}</p>
              </div>
              <div className="rounded-lg bg-[hsl(var(--ds-muted))] p-3">
                <p className="text-[hsl(var(--ds-muted-fg))]">RPU spec</p>
                <p className="font-semibold font-mono text-[11px]">
                  {filing?.rpuSpecKey || fvuResult?.engine?.rpuSpecKey || "—"}
                </p>
              </div>
              <div className="rounded-lg bg-[hsl(var(--ds-muted))] p-3 lg:col-span-2">
                <p className="text-[hsl(var(--ds-muted-fg))]">Path</p>
                <p className="font-semibold">RPU text → Protean/NSDL FVU jar → .fvu + Form 27A</p>
              </div>
              {filing?.rpuGeneratedAt && (
                <div className="rounded-lg bg-[hsl(var(--ds-muted))] p-3 sm:col-span-2 lg:col-span-4">
                  <p className="text-[hsl(var(--ds-muted-fg))]">Last RPU</p>
                  <p className="font-semibold">
                    {new Date(filing.rpuGeneratedAt).toLocaleString("en-IN")}
                    {filing.rpuFileName ? ` · ${filing.rpuFileName}` : ""}
                  </p>
                </div>
              )}
            </div>

            {fvuResult && !fvuResult.success && (
              <div className="mb-3 space-y-2">
                <ValidationList items={fvuResult.parsedErrors} type="error" />
                {fvuResult.message && (
                  <p className="text-xs text-amber-800">{fvuResult.message}</p>
                )}
              </div>
            )}

            {fvuResult?.sdValidation && (
              <div className="mb-3 space-y-2">
                <ValidationList
                  items={(fvuResult.sdValidation.errors || []).map((e) => ({
                    field: e.code || "SD",
                    message: e.message,
                  }))}
                  type="error"
                />
                <ValidationList
                  items={(fvuResult.sdValidation.warnings || []).map((w) => ({
                    field: w.code || "SD",
                    message: w.message,
                  }))}
                  type="warning"
                />
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <DsButton
                type="button"
                variant="outline"
                disabled={loading || isFiled}
                onClick={handleExportRpu}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <FileArchive size={16} />}
                Download RPU ZIP
              </DsButton>
              <DsButton
                type="button"
                variant="outline"
                disabled={
                  loading ||
                  isFiled ||
                  !filing?.id ||
                  (bundle && !bundle.reconciliation?.balanced)
                }
                onClick={handleExportForm138Excel}
                title="Form No. 138 workbook (Part A, Part B, Annexure I) after challan mapping"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <FileSpreadsheet size={16} />
                )}
                Download Form 138 Excel
              </DsButton>
              <DsButton
                type="button"
                variant="primary"
                disabled={loading || isFiled || !filing?.id}
                onClick={handleRunFvu}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                Run official FVU
              </DsButton>
              {fvuEntry?.downloadUrl && (
                <a
                  href={fvuEntry.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-[hsl(var(--ds-border))] px-3 text-sm font-medium"
                >
                  <Download size={16} /> Get FVU from NSDL
                  <ExternalLink size={12} className="opacity-50" />
                </a>
              )}
              <DsButton type="button" variant="outline" className="ml-auto" onClick={() => setStep(5)}>
                I uploaded to NSDL <ArrowRight size={14} />
              </DsButton>
            </div>

            {!!artifacts.length && (
              <div className="mt-4 rounded-lg border border-[hsl(var(--ds-border))] p-3">
                <p className="mb-2 text-xs font-semibold text-[hsl(var(--ds-fg))]">
                  Filing artifacts (durable)
                </p>
                <ul className="space-y-1 text-xs">
                  {artifacts.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[hsl(var(--ds-muted-fg))]">
                        {a.artifactType} v{a.version}
                      </span>
                      <span>{a.fileName || "file"}</span>
                      <button
                        type="button"
                        className="text-[hsl(var(--ds-primary))] underline"
                        onClick={async () => {
                          try {
                            const resp = await payrollOpsService.downloadForm24QArtifact(
                              filing.id,
                              a.id
                            );
                            const blob = new Blob([resp.data]);
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = a.fileName || `artifact-${a.id}`;
                            link.click();
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            toast.error(err?.response?.data?.message || "Download failed");
                          }
                        }}
                      >
                        Download
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 5 — Record filing */}
        {step === 5 && (
        <div className={`${card} min-h-0 flex-1 space-y-4 overflow-y-auto p-5`}>
          <div className="flex items-center gap-2">
            <Upload size={18} className="text-[hsl(var(--ds-primary))]" />
            <h3 className="text-sm font-semibold">Record NSDL / TRACES filing</h3>
          </div>

          {bundle?.reconciliation && (
            <div
              className={`rounded-lg border p-3 text-xs ${
                bundle.reconciliation.balanced
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-200 bg-red-50 text-red-900"
              }`}
            >
              Dashboard reconciliation — challan TDS{" "}
              {formatInr(bundle.reconciliation.totalChallanTds)} vs deductee TDS{" "}
              {formatInr(bundle.reconciliation.totalDeducteeTds)}
              {!bundle.reconciliation.balanced && " — fix before recording"}
            </div>
          )}

          {isFiled && filing?.returnType !== "CORRECTION" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <div className="flex items-center gap-2 font-semibold">
                <CheckCircle2 size={18} />
                Filed on{" "}
                {filing.filedAt ? new Date(filing.filedAt).toLocaleDateString("en-IN") : "—"}
              </div>
              <dl className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                {(filing.provisionalReceiptNumber || filing.nsdlAckNumber) && (
                  <div>
                    <dt className="text-emerald-700">PRN / Ack</dt>
                    <dd className="font-mono">
                      {filing.provisionalReceiptNumber || filing.nsdlAckNumber}
                    </dd>
                  </div>
                )}
                {filing.nsdlToken && (
                  <div>
                    <dt className="text-emerald-700">Token</dt>
                    <dd className="font-mono">{filing.nsdlToken}</dd>
                  </div>
                )}
                {filing.fvuVersion && (
                  <div>
                    <dt className="text-emerald-700">FVU version</dt>
                    <dd>{filing.fvuVersion}</dd>
                  </div>
                )}
              </dl>

              <div className="mt-4 border-t border-emerald-200 pt-3">
                <p className="mb-2 text-xs font-semibold">File Correction (revise filed return)</p>
                <p className="mb-2 text-[11px] text-emerald-800">
                  Imports accepted data from the filed return (Conso clone). Corrections are blocked
                  more than 2 years after FY end (rule from 1 Apr 2026).
                </p>
                <div className="mb-2 flex flex-wrap gap-2">
                  {(correctionTypesCatalog || []).map((c) => {
                    if (c.q4Only && quarter !== "Q4") return null;
                    const on = correctionTypes.includes(c.code);
                    return (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() =>
                          setCorrectionTypes((prev) =>
                            on ? prev.filter((x) => x !== c.code) : [...prev, c.code]
                          )
                        }
                        className={`rounded border px-2 py-1 text-[11px] ${
                          on ? "border-redhalo-red bg-white text-redhalo-red" : "border-emerald-300"
                        }`}
                      >
                        {c.code} — {c.label}
                      </button>
                    );
                  })}
                </div>
                <label className="mb-2 block text-[11px]">
                  Original PRN
                  <input
                    className={inputCls}
                    value={
                      originalPrn ||
                      filing.provisionalReceiptNumber ||
                      filing.nsdlAckNumber ||
                      ""
                    }
                    onChange={(e) => setOriginalPrn(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleStartCorrection}
                  className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white"
                >
                  Start correction return
                </button>
              </div>
            </div>
          )}

          {(!isFiled || filing?.returnType === "CORRECTION") && (
            <>
              <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                After uploading the .fvu on NSDL / TRACES, enter the Provisional Receipt Number (PRN)
                below to lock this quarter in PaceHRM.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-xs font-medium">
                  Provisional Receipt Number (PRN) *
                  <input
                    value={nsdl.provisionalReceiptNumber}
                    onChange={(e) =>
                      setNsdl((n) => ({
                        ...n,
                        provisionalReceiptNumber: e.target.value,
                        nsdlAckNumber: e.target.value,
                      }))
                    }
                    placeholder="From TRACES after upload"
                    className={inputCls}
                  />
                </label>
                <label className="block text-xs font-medium">
                  NSDL token
                  <input
                    value={nsdl.nsdlToken}
                    onChange={(e) => setNsdl((n) => ({ ...n, nsdlToken: e.target.value }))}
                    className={inputCls}
                  />
                </label>
                <label className="block text-xs font-medium">
                  Filing date on TRACES
                  <input
                    type="date"
                    value={nsdl.nsdlFiledDate}
                    onChange={(e) => setNsdl((n) => ({ ...n, nsdlFiledDate: e.target.value }))}
                    className={inputCls}
                  />
                </label>
                <label className="block text-xs font-medium sm:col-span-2">
                  Remarks
                  <textarea
                    value={challan.remarks}
                    onChange={(e) => setChallan((c) => ({ ...c, remarks: e.target.value }))}
                    rows={2}
                    className={inputCls}
                  />
                </label>
              </div>
              <DsButton
                type="button"
                variant="primary"
                onClick={handleFile}
                disabled={loading || !aggregates.length}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Record filing & lock quarter
              </DsButton>
            </>
          )}
        </div>
      )}

      </div>

      <DeductorMasterModal
        open={deductorModalOpen}
        onClose={(saved) => {
          setDeductorModalOpen(false);
        }}
        darkMode={darkMode}
        filingId={filing?.id || null}
        employees={employeeList}
      />

      <Form24QPortDataModal
        open={portDataModalOpen}
        onClose={() => setPortDataModalOpen(false)}
        darkMode={darkMode}
        financialYear={financialYear}
        quarter={quarter}
        filingId={filing?.id || null}
        initialPortData={portData}
        onPorted={(data) => setPortData(data)}
      />
    </div>
  );
}
